import type { PointerEvent as ReactPointerEvent } from 'react'
import type { ResizeHandle } from '@/lib/boundaries/bounding-box-boundary'
import type { Point } from '@/lib/shapes/shape-coordinates'

import { toClientPoint } from '../helpers/chrome-points'
import type { SelectionChromeLayerProps } from '../types/selection-chrome-layer-props'

/**
 * Pointer-down starters for the chrome's move, resize and rotate gestures, each forwarded straight
 * to `ShapeManager`. All of them ignore non-primary buttons; resize and rotate also do nothing
 * unless `gesturesEnabled`. `beginResize` is shared by the edge strips and the resize dots.
 */
export function createChromeGestureHandlers({
  shapeManagerRef,
  gesturesEnabled,
  pivot,
}: {
  shapeManagerRef: SelectionChromeLayerProps['shapeManagerRef']
  gesturesEnabled: boolean
  /** Rotation pivot in this layer's screen space, handed to the manager in client coordinates. */
  pivot: Point
}) {
  const beginMove = (event: ReactPointerEvent<SVGElement>) => {
    if (event.button !== 0) return
    shapeManagerRef?.current?.beginChromeMove?.(
      event as unknown as ReactPointerEvent<HTMLDivElement>
    )
  }

  const beginResize = (key: ResizeHandle) => (event: ReactPointerEvent<SVGElement>) => {
    if (event.button !== 0 || !gesturesEnabled) return
    shapeManagerRef?.current?.beginChromeResize?.(
      event as unknown as ReactPointerEvent<HTMLDivElement>,
      key
    )
  }

  const beginRotate = (event: ReactPointerEvent<SVGElement>) => {
    if (event.button !== 0 || !gesturesEnabled) return
    shapeManagerRef?.current?.beginChromeRotate?.(
      event as unknown as ReactPointerEvent<HTMLDivElement>,
      toClientPoint(event.currentTarget, pivot)
    )
  }

  return { beginMove, beginResize, beginRotate }
}
